

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTicketOpenClosedReport,
  exportTicketOpenClosedReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchDepartmentDropdown, fetchEmployeeDropdown, fetchTicketDropdown } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Ticket, CheckCircle, AlertTriangle, User, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';

const TicketOpenClosedReportPage = () => {
  const dispatch = useDispatch();

  const {
    ticketOpenClosedReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  // Get employees from Redux state
  const employees = useSelector((state) => state.common?.employees?.items || []);

  const ticketCode = useSelector((state) => state.common?.tickets.items || '');
  // Get loading state for employees
  const department = useSelector((state) => state.common?.departments?.items || false);

  const summary = ticketOpenClosedReport.data.summary || {};
  const results = ticketOpenClosedReport.data.results || [];
  const pagination = ticketOpenClosedReport.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    ticket_code: '',
    assigned_to: '',
    created_by_department: 'all',
    created_by_name: 'all',
    issue_type: 'all',
    priority: '',
    period_start: '',
    period_end: '',
  });

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'open_closed',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.ticket_code && { ticket_code: advancedFilters.ticket_code }),
      ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
      ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
      ...(advancedFilters.created_by_name && { created_by_name: advancedFilters.created_by_name }),
      ...(advancedFilters.issue_type && { issue_type: advancedFilters.issue_type }),
      ...(advancedFilters.priority && { priority: advancedFilters.priority }),
      ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
      ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
    };

    dispatch(fetchTicketOpenClosedReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all employees on component mount
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
    dispatch(fetchTicketDropdown());
    dispatch(fetchDepartmentDropdown());
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      ticket_code: '',
      assigned_to: '',
      created_by_department: 'all',
      created_by_name: 'all',
      issue_type: 'all',
      priority: '',
      period_start: '',
      period_end: '',
    });
  };

  const columns = [
    { accessorKey: 'ticket_code', header: 'Ticket Code' },
    { accessorKey: 'title', header: 'Title' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        let variant = 'secondary';
        if (status === 'open') variant = 'default';
        if (status === 'in_progress') variant = 'default';
        if (status === 'closed') variant = 'outline';
        if (status === 'escalated') variant = 'destructive';
        return (
          <Badge variant={variant}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => {
        const priority = row.original.priority;
        let variant = 'secondary';
        if (priority === 'high') variant = 'destructive';
        if (priority === 'medium') variant = 'default';
        return (
          <Badge variant={variant}>
            {priority}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (formatDateTime(row.original.created_at) ?? '-'),
    },
    {
      accessorKey: 'assigned_to_name',
      header: 'Assigned To',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.assigned_to_name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'resolved_remark',
      header: 'Resolved Remark',
      cell: ({ row }) => (
         row.original.resolved_remark ?? '-'
       
      ),
    },
      {
      accessorKey: 're_opened_at',
      header: 'Reopened At',
      cell: ({ row }) => (formatDateTime(row.original.re_opened_at) ?? '-')
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Open vs Closed Tickets Report"
        description="Track ticket lifecycle from creation to resolution with detailed status tracking"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={advancedFilters.status}
                  onValueChange={(value) => handleAdvancedFilterChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticket_code">Ticket Code</Label>
                <Select
                  value={advancedFilters.ticket_code}
                  onValueChange={(value) => handleAdvancedFilterChange('ticket_code', value)}

                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Code" />
                  </SelectTrigger>
                  <SelectContent>
                    
                    {Array.isArray(ticketCode) && ticketCode.map((code) => (
                      <SelectItem key={code.id} value={code.ticket_code}>
                        {code.ticket_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assigned_to">Assigned To</Label>
                <Select
                  value={advancedFilters.assigned_to}
                  onValueChange={(value) => handleAdvancedFilterChange('assigned_to', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                  
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="created_by_department">Created By Department</Label>
                <Select
                  value={advancedFilters.created_by_department}
                  onValueChange={(value) => handleAdvancedFilterChange('created_by_department', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(department) && department.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              <FormField
  label="Status"
  name="status"
  type="select"
  value={advancedFilters.status}
  onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
  options={[
    { value: 'all', label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' },
    { value: 'escalated', label: 'Escalated' },
        { value: 'ack_pending', label: 'Awaiting Acknowledgement' },
  ]}
/>

<FormField
  label="Ticket Code"
  name="ticket_code"
  type="select"
  value={advancedFilters.ticket_code}
  onChange={(e) => handleAdvancedFilterChange('ticket_code', e.target.value)}
  options={[
    ...(Array.isArray(ticketCode)
      ? ticketCode.map((code) => ({
          value: code.ticket_code,
          label: code.ticket_code,
        }))
      : []),
  ]}
/>
<FormField
  label="Assigned To"
  name="assigned_to"
  type="select"
  value={advancedFilters.assigned_to}
  onChange={(e) => handleAdvancedFilterChange('assigned_to', e.target.value)}
  options={[
    ...(Array.isArray(employees)
      ? employees.map((employee) => ({
          value: String(employee.id),
          label: employee.name,
        }))
      : []),
  ]}
/>

{/* <FormField
  label="Created By Department"
  name="created_by_department"
  type="select"
  value={advancedFilters.created_by_department}
  onChange={(e) =>
    handleAdvancedFilterChange('created_by_department', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    ...(Array.isArray(department)
      ? department.map((dep) => ({
          value: String(dep.id),
          label: dep.name,
        }))
      : []),
  ]}
/> */}


              {/* <div className="space-y-2">
                <Label htmlFor="created_by_name">Created By Name</Label>
                <Select
                  value={advancedFilters.created_by_name}
                  onValueChange={(value) => handleAdvancedFilterChange('created_by_name', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="issue_type">Issue Type</Label>
                <Select
                  value={advancedFilters.issue_type}
                  onValueChange={(value) => handleAdvancedFilterChange('issue_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="enhancement">Enhancement</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="incident">Incident</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={advancedFilters.priority}
                  onValueChange={(value) => handleAdvancedFilterChange('priority', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* <SelectItem value="all">All</SelectItem> */}
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* <div className="space-y-2">
                <Label>Period Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="period_start">Start Date</Label>
                    <Input
                      type="date"
                      placeholder="Start date"
                      value={advancedFilters.period_start}
                      onChange={(e) => handleAdvancedFilterChange('period_start', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="period_end">End Date</Label>
                    <Input
                      type="date"
                      placeholder="End date"
                      value={advancedFilters.period_end}
                      onChange={(e) => handleAdvancedFilterChange('period_end', e.target.value)}
                    />
                  </div>
                </div>
              </div> */}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
            <CheckCircle className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {summary.open || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.in_progress || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Tickets</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary.closed || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Escalated</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.escalated || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading ticket data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by ticket code, title..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportTicketOpenClosedReport({
              filters: {
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'open_closed',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
                ...(advancedFilters.ticket_code && { ticket_code: advancedFilters.ticket_code }),
                ...(advancedFilters.assigned_to && { assigned_to: advancedFilters.assigned_to }),
                ...(advancedFilters.created_by_department && { created_by_department: advancedFilters.created_by_department }),
                ...(advancedFilters.created_by_name && { created_by_name: advancedFilters.created_by_name }),
                ...(advancedFilters.issue_type && { issue_type: advancedFilters.issue_type }),
                ...(advancedFilters.priority && { priority: advancedFilters.priority }),
                ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
                ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
              }
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}
    </div>
  );
};

export default TicketOpenClosedReportPage;